import { useCallback, useEffect, useState } from 'react';
import { MessageCircleQuestion, Send } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Textarea } from '@/components/ui/Field';
import { EmptyState, Spinner } from '@/components/ui/EmptyState';
import { useToast } from '@/components/ui/ToastContext';
import { supabase } from '@/lib/supabaseClient';
import { rxkModuleIcon, rxkModuleTitle, rxkNavGroups } from '../nav';

interface QnaRow {
  id: string;
  category: string;
  question: string;
  status: string;
  answer: string | null;
  created_at: string;
  customer_id: string;
  product_sku: string | null;
  rxk_customers: { full_name: string; email: string | null } | null;
}

export default function RxkQna() {
  const { push } = useToast();
  const [items, setItems] = useState<QnaRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [replyingId, setReplyingId] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('rxk_qna')
      .select('id, category, question, status, answer, created_at, customer_id, product_sku, rxk_customers(full_name, email)')
      .order('created_at', { ascending: false });
    setItems((data as unknown as QnaRow[]) ?? []);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openReply = (row: QnaRow) => {
    setReplyingId(row.id);
    setReplyText(row.answer ?? '');
  };

  const submitReply = async (id: string) => {
    if (!replyText.trim()) return;
    setBusy(true);
    const {
      data: { user },
    } = await supabase.auth.getUser();
    const { error } = await supabase
      .from('rxk_qna')
      .update({ answer: replyText.trim(), status: 'answered', answered_at: new Date().toISOString(), answered_by: user?.id ?? null })
      .eq('id', id);
    setBusy(false);
    if (error) {
      push('error', error.message);
      return;
    }
    push('success', 'Reply sent');
    setReplyingId(null);
    setReplyText('');
    await load();
  };

  const pendingCount = items.filter((i) => i.status === 'pending').length;

  return (
    <AppShell moduleTitle={rxkModuleTitle} moduleIcon={rxkModuleIcon} navGroups={rxkNavGroups}>
      <div className="space-y-5">
        <h1 className="text-[22px] font-bold text-ink">Customer Q&A (고객 문의)</h1>

        <Card>
          <CardHeader
            title="Questions"
            subtitle={`${pendingCount} pending · ${items.length} total`}
            icon={<MessageCircleQuestion className="w-4 h-4" />}
          />
          <CardBody className="space-y-2">
            {loading ? (
              <Spinner />
            ) : items.length === 0 ? (
              <EmptyState title="No questions yet" />
            ) : (
              items.map((row) => (
                <div key={row.id} className="p-3 rounded-lg border border-border-subtle">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-[12.5px] font-semibold text-ink truncate">
                        {row.rxk_customers?.full_name ?? 'Unknown customer'}
                        <span className="text-ink-faint font-normal"> · {row.rxk_customers?.email}</span>
                      </p>
                      <p className="text-[11px] text-ink-faint">
                        {row.created_at.slice(0, 10)}
                        {row.product_sku && <span> · {row.product_sku}</span>}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Badge tone="neutral">{row.category}</Badge>
                      <Badge tone={row.status === 'answered' ? 'success' : 'warning'}>{row.status}</Badge>
                    </div>
                  </div>
                  <p className="text-[13px] text-ink mt-2">{row.question}</p>

                  {row.answer && replyingId !== row.id && (
                    <div className="mt-2 p-2.5 rounded-lg bg-surface-muted text-[12.5px] text-ink-muted">{row.answer}</div>
                  )}

                  {replyingId === row.id ? (
                    <div className="mt-2 space-y-2">
                      <Textarea
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        rows={3}
                        placeholder="Write a reply…"
                      />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => submitReply(row.id)} disabled={busy || !replyText.trim()}>
                          <Send className="w-3.5 h-3.5" /> Send
                        </Button>
                        <Button size="sm" variant="secondary" onClick={() => setReplyingId(null)} disabled={busy}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button size="sm" variant="secondary" className="mt-2" onClick={() => openReply(row)}>
                      {row.status === 'answered' ? 'Edit reply' : 'Reply'}
                    </Button>
                  )}
                </div>
              ))
            )}
          </CardBody>
        </Card>
      </div>
    </AppShell>
  );
}
